import * as React from "react"
import { Col, Container, Hidden, Row, useScreenClass, Visible } from "react-grid-system"
import styled from "styled-components"
import H1 from "../../atoms/typography/H1"
import { HorizontalSpacer } from "../../atoms/spacers"
import H3 from "../../atoms/typography/H3"
import { theme } from "../../theme"
import H2 from "../../atoms/typography/H2"
import ColorfulButton from "../../atoms/buttons/ColorfulButton"
import Slider from "react-slick"
import { MediaQuery } from "../../../utils/design-helpers"
import { useNavigate } from "react-router-dom"

const TopBanner = styled.div`
  padding: 1rem;
  background-color: ${(props) => props.theme.colors.dark};
  width: 100%;
`

const ColoredTitle = styled(H1)<{ color: string }>`
  background: ${(props) => props.color};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`

const ColoredTitleMobile = styled(H1)<{ color: string }>`
  text-align: center;
  background: ${(props) => props.color};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

const MenuSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex: 0.5;

  ${MediaQuery.MD} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0.5;
  }

  ${MediaQuery.SM} {
    flex-direction: column;
    justify-content: center;
    flex: 1;
  }
`

const MenuItem = styled.div`
  min-width: 89px;
  cursor: pointer;

  :hover {
    filter: opacity(0.6);
  }

  ${MediaQuery.MD} {
    margin: 0.5rem;
  }
`

const ActiveItemBar = styled.div<{ color?: any }>`
  height: 6px;
  border-radius: 3px;
  background-color: ${(props) => props.theme.colors.greyDisabled};
  background: ${(props) => (props.color ? props.color : undefined)};
  width: 100%;
`

const SlideImage = styled.img`
  width: 100%;
  height: 80vh;
  object-fit: cover;
`

const ImageContainer = styled.div`
  position: relative;
`

const TextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const SmallContainer = styled(Container)`
  ${MediaQuery.SM} {
    width: 75vw !important;
  }
  ${MediaQuery.XS} {
    width: 85vw !important;
  }
`

const ProductsPage: React.FC = () => {
  const navigate = useNavigate()

  const sliderRef = React.useRef<Slider>(null)
  const [currentSlide, setCurrentSlide] = React.useState(0)

  const goToSpecificSlide = (slideNumber: number) => {
    //@ts-ignore
    sliderRef.current.slickGoTo(slideNumber)
  }

  const handleSliderAfterChange = (current: number, slideIndex: number) => {
    setCurrentSlide(slideIndex)
  }

  var settings = {
    infinite: true,
    speed: 1100,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (current: number, next: number) => handleSliderAfterChange(current, next),
    arrows: false, // this setting removes the "previous" and "next" buttons that are shown by default
    autoplay: true,
    autoplaySpeed: 2600,
    responsive: [
      {
        breakpoint: 576,
        settings: {
          centerMode: true,
          centerPadding: "0",
          slidesToScroll: 1,
          slidesToShow: 1,
        },
      },
    ],
  }

  const textColor =
    currentSlide === 0 ? theme.gradients.blue : currentSlide === 1 ? theme.gradients.green : theme.gradients.red

  const screenClass = useScreenClass()

  return (
    <div style={{ marginBottom: -8 }}>
      <TopBanner>
        <HorizontalSpacer spacing={2} />
        <Container>
          <Row nogutter justify="between" direction={screenClass === "xs" || screenClass === "sm" ? "column" : "row"}>
            <Hidden xs sm md>
              <ColoredTitle color={textColor}>Products</ColoredTitle>
            </Hidden>
            <Visible xs sm md>
              <ColoredTitleMobile color={textColor}>Products</ColoredTitleMobile>
            </Visible>
            <MenuSection>
              <MenuItem onClick={() => goToSpecificSlide(0)}>
                <H3 color={currentSlide === 0 ? theme.colors.white : theme.colors.greyDisabled} textAlign="center">
                  Satcube Ku
                </H3>
                <HorizontalSpacer spacing={1} />
                <ActiveItemBar color={currentSlide === 0 && theme.gradients.blue} />
              </MenuItem>
              <MenuItem onClick={() => goToSpecificSlide(1)}>
                <H3 color={currentSlide === 1 ? theme.colors.white : theme.colors.greyDisabled} textAlign="center">
                  Satcube Secure
                </H3>
                <HorizontalSpacer spacing={1} />
                <ActiveItemBar color={currentSlide === 1 && theme.gradients.green} />
              </MenuItem>
              <MenuItem onClick={() => goToSpecificSlide(2)}>
                <H3 color={currentSlide === 2 ? theme.colors.white : theme.colors.greyDisabled} textAlign="center">
                  Satcube Motion
                </H3>
                <HorizontalSpacer spacing={1} />
                <ActiveItemBar color={currentSlide === 2 && theme.gradients.red} />
              </MenuItem>
              <MenuItem onClick={() => goToSpecificSlide(3)}>
                <H3 color={currentSlide === 3 ? theme.colors.white : theme.colors.greyDisabled} textAlign="center">
                  Bespoke Networks
                </H3>
                <HorizontalSpacer spacing={1} />
                <ActiveItemBar color={currentSlide === 3 && theme.gradients.blue} />
              </MenuItem>
            </MenuSection>
          </Row>
        </Container>
        <HorizontalSpacer spacing={2} />
      </TopBanner>
      <Slider ref={sliderRef} {...settings}>
        <div>
          <ImageContainer>
            <SlideImage src="https://images.ctfassets.net/kzqg847wz5jr/4KWuwV1pELYBa2RmTxDeIW/e77151405340a2050f3cdbd51d3ccb7c/burned-forest_new_logo.webp" />
            <TextContainer>
              <SmallContainer>
                <Row id="row123">
                  <Col>
                    <H1 color="white">Satcube Ku</H1>
                    <H2 color="white">Portable satellite broadband</H2>
                    <HorizontalSpacer spacing={1} />
                    <ColorfulButton variant="blue" title="Learn more" onClick={() => navigate("/products/ku")} />
                  </Col>
                </Row>
              </SmallContainer>
            </TextContainer>
          </ImageContainer>
        </div>
        <div>
          <ImageContainer>
            <SlideImage src="https://images.ctfassets.net/kzqg847wz5jr/1dRjqo9u8iyynlwbFMRwfP/abfae95c385bf14d938d0358145fc6b9/secure-produktbild.webp" />
            <TextContainer>
              <SmallContainer>
                <Row>
                  <Col>
                    <H1 color="white">Satcube Secure</H1>
                    <H2 color="white">For operations in TRANSEC networks</H2>
                    <HorizontalSpacer spacing={1} />
                    <ColorfulButton variant="green" title="Learn more" onClick={() => navigate("/products/secure")} />
                  </Col>
                </Row>
              </SmallContainer>
            </TextContainer>
          </ImageContainer>
        </div>
        <div>
          <ImageContainer>
            <SlideImage
              src="https://images.ctfassets.net/kzqg847wz5jr/2E9Up4bmRk0cRqean3klqD/6dc4642a1353ff18c404c3f840b5d099/Motion_-rgb-expanded2_copy-smaller.webp"
              style={{ objectPosition: "center 35%" }}
            />
            <TextContainer>
              <SmallContainer>
                <Row>
                  <Col>
                    <H1 color="white">Satcube Motion</H1>
                    <H2 color="white">Satellite broadband, now on-the-move</H2>
                    <HorizontalSpacer spacing={1} />
                    <ColorfulButton variant="red" title="Learn more" onClick={() => navigate("/products/motion")} />
                  </Col>
                </Row>
              </SmallContainer>
            </TextContainer>
          </ImageContainer>
        </div>
        <div>
          <ImageContainer>
            <SlideImage
              src="https://images.ctfassets.net/kzqg847wz5jr/6FGcnkna1QXpajiGo6VxZf/234f092efa89324934d3f82294b496b2/Networks-header.webp"
              style={{ objectPosition: "center 35%" }}
            />
            <TextContainer>
              <SmallContainer>
                <Row>
                  <Col>
                    <H1 color="white">Bespoke Networks</H1>
                    <H2 color="white">Bespoke satellite networks for secure communications</H2>
                    <HorizontalSpacer spacing={1} />
                    <ColorfulButton variant="blue" title="Learn more" onClick={() => navigate("/products/network")} />
                  </Col>
                </Row>
              </SmallContainer>
            </TextContainer>
          </ImageContainer>
        </div>
      </Slider>
      <link
        rel="stylesheet"
        type="text/css"
        charSet="UTF-8"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
      />
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
      />
    </div>
  )
}

export default ProductsPage
